import { z } from "zod";
export const BATON_PROTOCOL_VERSION = 1;
export const MINIMUM_BRIDGE_VERSION = "0.1.0";
export const BRIDGE_DISTRIBUTION = Object.freeze({
    channel: "npm",
    packageName: "@baton/bridge",
    stableTag: "latest",
});
const SemverSchema = z.string().regex(/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/);
export const BridgeHandshakeSchema = z.object({
    protocolVersion: z.number().int().positive(),
    bridgeVersion: SemverSchema,
    distributionChannel: z.literal(BRIDGE_DISTRIBUTION.channel),
});
export const HandshakePolicySchema = z.object({
    currentProtocolVersion: z.number().int().positive(),
    minimumProtocolVersion: z.number().int().positive(),
    minimumBridgeVersion: SemverSchema,
});
export const HandshakeResultSchema = z.discriminatedUnion("accepted", [
    z.object({
        accepted: z.literal(true),
        protocolVersion: z.number().int().positive(),
        minimumBridgeVersion: SemverSchema,
        update: z.object({
            channel: z.literal("npm"),
            packageName: z.literal("@baton/bridge"),
            stableTag: z.literal("latest"),
        }),
    }),
    z.object({
        accepted: z.literal(false),
        code: z.enum(["protocol_too_old", "protocol_too_new", "bridge_too_old"]),
        protocolVersion: z.number().int().positive(),
        minimumBridgeVersion: SemverSchema,
        update: z.object({
            channel: z.literal("npm"),
            packageName: z.literal("@baton/bridge"),
            stableTag: z.literal("latest"),
        }),
    }),
]);
export function evaluateBridgeHandshake(rawHandshake, policy = {
    currentProtocolVersion: BATON_PROTOCOL_VERSION,
    minimumProtocolVersion: BATON_PROTOCOL_VERSION,
    minimumBridgeVersion: MINIMUM_BRIDGE_VERSION,
}) {
    const handshake = BridgeHandshakeSchema.parse(rawHandshake);
    const base = {
        protocolVersion: policy.currentProtocolVersion,
        minimumBridgeVersion: policy.minimumBridgeVersion,
        update: BRIDGE_DISTRIBUTION,
    };
    if (handshake.protocolVersion < policy.minimumProtocolVersion) {
        return { accepted: false, code: "protocol_too_old", ...base };
    }
    if (handshake.protocolVersion > policy.currentProtocolVersion) {
        return { accepted: false, code: "protocol_too_new", ...base };
    }
    if (compareSemver(handshake.bridgeVersion, policy.minimumBridgeVersion) < 0) {
        return { accepted: false, code: "bridge_too_old", ...base };
    }
    return { accepted: true, ...base };
}
function compareSemver(left, right) {
    const [leftCore, leftPrerelease] = splitSemver(left);
    const [rightCore, rightPrerelease] = splitSemver(right);
    const leftParts = leftCore.split(".").map(Number);
    const rightParts = rightCore.split(".").map(Number);
    for (let index = 0; index < 3; index += 1) {
        const difference = leftParts[index] - rightParts[index];
        if (difference !== 0)
            return Math.sign(difference);
    }
    if (leftPrerelease === undefined && rightPrerelease === undefined)
        return 0;
    if (leftPrerelease === undefined)
        return 1;
    if (rightPrerelease === undefined)
        return -1;
    const leftIdentifiers = leftPrerelease.split(".");
    const rightIdentifiers = rightPrerelease.split(".");
    const length = Math.max(leftIdentifiers.length, rightIdentifiers.length);
    for (let index = 0; index < length; index += 1) {
        const leftIdentifier = leftIdentifiers[index];
        const rightIdentifier = rightIdentifiers[index];
        if (leftIdentifier === undefined)
            return -1;
        if (rightIdentifier === undefined)
            return 1;
        if (leftIdentifier === rightIdentifier)
            continue;
        const leftNumeric = /^\d+$/.test(leftIdentifier);
        const rightNumeric = /^\d+$/.test(rightIdentifier);
        if (leftNumeric && rightNumeric)
            return Math.sign(Number(leftIdentifier) - Number(rightIdentifier));
        if (leftNumeric !== rightNumeric)
            return leftNumeric ? -1 : 1;
        return leftIdentifier < rightIdentifier ? -1 : 1;
    }
    return 0;
}
function splitSemver(value) {
    const separator = value.indexOf("-");
    return separator === -1
        ? [value, undefined]
        : [value.slice(0, separator), value.slice(separator + 1)];
}
//# sourceMappingURL=protocol.js.map